import { Injectable } from '@angular/core';
import {StockDataSourceService} from '../data-sources/stock-data-source.service';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import { V3CrateCommentsDataSourceService } from '../data-sources/v3/crates-comments/crate-comments-data-source.service';
import { CrateComments } from '../../../models/CrateComments';
import { Crate, CrateStateEnum } from '../../../models/Crate';

const PAGE_SIZE_OPTIONS = [30, 60, 90];

export interface StockFilterParams {
  sku?: string;
  state?: CrateStateEnum;
  section?: string;
  levelName?: string;
  name?: string;
  x?: number;
  y?: number;
  receivedAtEndDate?: number;
  receivedAtStartDate?: number;
  vendorDoc?: string;
  quantity?: number;
  tagRfid?: string;
  alias?: string;
  lastMovedAtStartDate?: number,
  lastMovedAtEndDate?: number;
  productName?: string;
  productDescription?: string;
}

export interface StockSortParams {
  orderBy: string;
  sortBy: string
}

@Injectable({
  providedIn: 'root'
})
export class StockService {

  crateCommentsQty$: BehaviorSubject<CrateComments[]> = new BehaviorSubject([]);
  private $pageSize: BehaviorSubject<number> = new BehaviorSubject<number>(PAGE_SIZE_OPTIONS[0]);
  private $totalItems: Subject<number> = new Subject<number>();
  private $pageCount: Subject<number> = new Subject<number>();
  public $currentPage: Subject<number> = new Subject<number>();
  private $atLastPage: Subject<boolean> = new Subject<boolean>();
  private $atFirstPage: Subject<boolean> = new Subject<boolean>();
  private $items: Subject<any[]> = new Subject<any[]>();
  private $pageSizeOptions: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  private pageSize = PAGE_SIZE_OPTIONS[0]; 
  private pageSizeOptions = PAGE_SIZE_OPTIONS;
  private pageCount;
  public currentPage;
  private totalItems;
  private items: [] = [];

  constructor(private dataSource: StockDataSourceService, private commentDataSource: V3CrateCommentsDataSourceService) {
    this.$pageSize.next(this.pageSize);
    this.$pageSizeOptions.next(this.pageSizeOptions);
  }

  async init(filter: StockFilterParams = null){
    return this.getPage(1, filter);
  }

  async setPageSize(pageSize: number){
    this.pageSize = pageSize;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$pageSize.next(this.pageSize);
    await this.getPage(1);
  }

  async reload(filter: StockFilterParams = null, sortParams?: StockSortParams){
    return await this.getPage(this.currentPage, filter, sortParams);
  }

  async getCSVStorage(filter:StockFilterParams = null){
    return await this.dataSource.getCSVStorageV2('STOCK', filter);
  }

  async getNextPage(filter: StockFilterParams = null, sortParams: StockSortParams){
    if (this.currentPage < this.pageCount){
      const params = {};
      Object.assign(params, filter);
      Object.assign(params, sortParams);
      return await this.getPage(this.currentPage + 1, params);
    } else{
      throw Error('???');
    }
  }
  async getPreviousPage(filter: StockFilterParams = null, sortParams: StockSortParams){
    if (this.currentPage > 1){
      const params = {};
      Object.assign(params, filter);
      Object.assign(params, sortParams);
      return await this.getPage(this.currentPage - 1, params);
    } else{
      throw Error('???');
    }
  }

  async setPage(page: number, filter: StockFilterParams = null, sortParams: StockSortParams){
    this.currentPage = page;
    const params = {};
    Object.assign(params, filter);
    Object.assign(params, sortParams);
    return await this.getPage(this.currentPage, params);
  }


  private async getPage(page: number = 1, filter: StockFilterParams = {}, sortParams?: StockSortParams){
    this.currentPage = page;
    const stockParams = {
      limit: this.pageSize ,
      offset: (this.currentPage - 1) * this.pageSize
    };
    Object.assign(stockParams, filter);
    Object.assign(stockParams, sortParams);
    const response = await this.dataSource.getStockCratesV2("STOCK", stockParams);
    this.totalItems = response.count;
    this.items = response.crates;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$items.next(this.items);
    this.$pageCount.next(this.pageCount);
    this.$totalItems.next(this.totalItems);
    this.$currentPage.next(this.currentPage);
    this.$atLastPage.next(this.pageCount === 0 || this.currentPage === this.pageCount);
    this.$atFirstPage.next(this.currentPage === 1);
    this.getCrateCommentsQty(this.items);
  }

  getCrateCommentsQty(crates: Crate[]) {
    if (crates.length) {
      const cratesIds = crates.map(c => c.id);
      if (cratesIds.length) {
        this.commentDataSource.getAll(cratesIds).subscribe(comments => {
          this.crateCommentsQty$.next(comments);
        });
      }
    }
  }

  get currentPage$(): Observable<number>{
      return this.$currentPage;
  }
  get atFirstPage$(): Observable<boolean>{
    return this.$atFirstPage;
  }
  get atLastPage$(): Observable<boolean>{
    return this.$atLastPage;
  }
  get totalItems$(): Observable<number>{
    return this.$totalItems;
  }
  get pageCount$(): Observable<number> {
    return this.$pageCount;
  }
  get items$(): Observable<any[]>{
    return this.$items;
  }

  get pageSize$(): Observable<number> {
    return this.$pageSize;
  }

  get pageSizeOptions$(): Observable<number[]> {
    return this.$pageSizeOptions;
  }

}
